import type { ComparisonDeckStats, PlayerProfile } from '@sleeved/lib';
import {
  palette,
  glass,
  glassBorder,
  glassText,
  fonts,
} from '../../styles/overlayTokens';
import type { AnimPhase } from './chartPrimitives';
import {
  p1rgba,
  p2rgba,
  makeAnimCls,
  SectionDivider,
  ColorPips,
  DonutTypeChart,
  MiniHeader,
  MkDistributionBar,
  MiniHorizontalBars,
  FeaturedCardAvatarCircle,
  DensePanelPlaceholder,
} from './chartPrimitives';

// ---------------------------------------------------------------------------
// digimon — DigimonDensePanel + DigimonRailPanel. Split out of the former
// DeckOverlayPanels.tsx monolith (pure move, no behavior change).
// ---------------------------------------------------------------------------

// ── D92: Dense panels ─────────────────────────────────────────────────────────

// ---------------------------------------------------------------------------
// DigimonDensePanel — dense half-width compare panel for Digimon Card Game.
// Sections: identity (single circle) → colors → card types → level/form →
//           trait breakdown → special mechanics.
// Per the design spec: no `if (gameId ===)` branching inside this component.
// ---------------------------------------------------------------------------

export interface DigimonDensePanelProps {
  side: 'p1' | 'p2';
  stats: ComparisonDeckStats | null;
  peerHasStats: boolean;
  playerName: string;
  archetype: string;
  phase: AnimPhase;
  reducedMotion: boolean;
  /** D92 Phase 2 field: Digimon featured card image for identity avatar. */
  featuredCardImageUrl?: PlayerProfile['featuredCardImageUrl'];
  /** Shared max for MkDistributionBar — computed at overlay level across both players. */
  levelSharedMax: number;
  /** Label union for level legend consistency — accepted from overlay (see implementation note). */
  levelLabelUnion: string[];
  fullWidth?: boolean;
  naturalLayout?: boolean;
  placeholderText?: string;
}

export function DigimonDensePanel({
  side,
  stats,
  peerHasStats,
  playerName,
  archetype,
  phase,
  reducedMotion,
  featuredCardImageUrl,
  // levelSharedMax and levelLabelUnion are accepted per spec; MkDistributionBar is proportional
  // (not absolute) so it does not consume sharedMax. labelUnion is not applied to avoid
  // rendering 0-count level labels in the legend for the peer's absent levels.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  levelSharedMax: _levelSharedMax,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  levelLabelUnion: _levelLabelUnion,
  fullWidth,
  naturalLayout,
  placeholderText,
}: DigimonDensePanelProps) {
  const animCls = makeAnimCls(phase);
  const isEntering = phase === 'entering';
  const isP1 = side === 'p1';
  const isP1Layout = isP1 || !!naturalLayout;
  const accentColor = isP1 ? palette.coralGlow : palette.brilliantRose;
  const accentRgba = isP1 ? p1rgba : p2rgba;
  const hasStats = stats !== null;
  const hasName = playerName.trim().length > 0;
  const isOneDeckPlaceholder = !hasStats && peerHasStats;

  // Data lookups
  const traitDist = stats?.subtypeDistributions?.[0] ?? null;
  const top5Traits = traitDist?.data
    ? [...traitDist.data].sort((a, b) => b.count - a.count).slice(0, 5)
    : [];
  const traitMax = top5Traits.length > 0 ? Math.max(...top5Traits.map(e => e.count)) : 1;
  const traitHeader = traitDist ? traitDist.label.replace(/ Breakdown$/i, '').toUpperCase() : 'TRAIT BREAKDOWN';

  const mechanics = stats?.specialMechanicCounts ?? null;
  const mechanicsMax = mechanics && mechanics.length > 0 ? Math.max(...mechanics.map(e => e.count)) : 1;

  const levelHeader = stats?.costCurveLabel ? stats.costCurveLabel.toUpperCase() : 'LEVEL / FORM DISTRIBUTION';

  return (
    <div
      aria-hidden="true"
      className={animCls(isP1Layout ? 'dcao-p1-enter' : 'dcao-p2-enter')}
      style={{
        flex: fullWidth ? 1 : '0 0 680px',
        maxWidth: fullWidth ? 1400 : undefined,
        margin: fullWidth ? '0 auto' : undefined,
        height: '100%',
        position: 'relative',
        background: glass.card,
        borderRadius: 16,
        border: `1px solid ${glassBorder.light}`,
        overflow: 'hidden',
        ...(isP1Layout
          ? { borderRight: fullWidth ? undefined : `2px solid ${accentColor}`, boxShadow: fullWidth ? undefined : `inset -4px 0 20px ${accentRgba(0.12)}` }
          : { borderLeft: fullWidth ? undefined : `2px solid ${accentColor}`, boxShadow: fullWidth ? undefined : `inset 4px 0 20px ${accentRgba(0.12)}` }),
      }}
    >
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, borderRadius: '16px 16px 0 0', background: accentColor }} />
      <div
        id={`dcao-content-${side}`}
        style={{
          position: 'absolute', top: 4, left: 0, right: 0, bottom: 0,
          padding: '20px 36px',
          display: 'flex', flexDirection: 'column',
          justifyContent: 'flex-start', overflow: 'hidden',
        }}
      >
        {/* Identity zone */}
        <div
          className={animCls('dcao-names-enter')}
          style={{
            display: 'flex',
            flexDirection: isP1Layout ? 'row' : 'row-reverse',
            alignItems: 'center',
            gap: 16,
            minHeight: 96,
            width: '100%',
            flexShrink: 0,
          }}
        >
          <FeaturedCardAvatarCircle
            imageUrl={featuredCardImageUrl}
            circleSize={80}
            accentColor={accentColor}
            accentRgba={accentRgba}
            isOneDeckPlaceholder={isOneDeckPlaceholder}
            reducedMotion={reducedMotion}
          />
          <div style={{
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
            minWidth: 0, flex: 1,
            alignItems: isP1Layout ? 'flex-start' : 'flex-end',
            textAlign: isP1Layout ? 'left' : 'right',
          }}>
            {hasName ? (
              <div style={{ fontSize: 36, fontFamily: fonts.display, fontWeight: 700, color: glassText.primary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.0, marginBottom: 3, width: '100%' }}>
                {playerName}
              </div>
            ) : (
              <div style={{ fontSize: 16, fontWeight: 400, color: glassText.muted, marginBottom: 3 }}>
                {isP1 ? 'Player 1' : 'Player 2'}
              </div>
            )}
            {archetype && (
              <div style={{ fontSize: 18, fontWeight: 500, color: glassText.secondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                {archetype}
              </div>
            )}
            {!hasStats && !archetype && (
              <div style={{ fontSize: 14, fontWeight: 400, color: glassText.disabled, fontStyle: 'italic' }}>
                {hasName ? 'Awaiting deck...' : 'No deck loaded'}
              </div>
            )}
          </div>
        </div>

        {hasStats ? (
          <>
            {/* Color Distribution */}
            <div style={{ marginTop: 10, flexShrink: 0 }}>
              <MiniHeader text="COLOR" />
              <ColorPips dist={stats.colorDistribution} side={isP1Layout ? 'p1' : 'p2'} phase={phase} />
            </div>

            {/* Card Types */}
            {stats.typeDistribution.length > 0 && (
              <div style={{ marginTop: 10, flexShrink: 0 }}>
                <MiniHeader text="CARD TYPES" />
                <DonutTypeChart
                  dist={stats.typeDistribution}
                  side={isP1Layout ? 'p1' : 'p2'}
                  phase={phase}
                  size={90}
                  legendPosition="side"
                  showTypePercentages={true}
                />
              </div>
            )}

            {/* Level / Form Distribution */}
            {stats.costCurve.length > 0 && (
              <div style={{ marginTop: 10, flexShrink: 0 }}>
                <MiniHeader text={levelHeader} />
                <MkDistributionBar
                  curve={stats.costCurve}
                  side={isP1Layout ? 'p1' : 'p2'}
                  accentColor={accentColor}
                  phase={phase}
                  reverse={!isP1Layout}
                />
              </div>
            )}

            {/* Trait Breakdown — top 5 */}
            {top5Traits.length > 0 && (
              <div style={{ marginTop: 10, flexShrink: 0 }}>
                <MiniHeader text={traitHeader} />
                <MiniHorizontalBars
                  entries={top5Traits}
                  localMax={traitMax}
                  side={isP1Layout ? 'p1' : 'p2'}
                  accentColor={accentColor}
                  phase={phase}
                  isEntering={isEntering}
                  startDelay={680}
                />
              </div>
            )}

            {/* Special Mechanics */}
            {mechanics && mechanics.length > 0 && (
              <div style={{ marginTop: 10, flexShrink: 0 }}>
                <MiniHeader text="SPECIAL MECHANICS" />
                <MiniHorizontalBars
                  entries={mechanics}
                  localMax={mechanicsMax}
                  side={isP1Layout ? 'p1' : 'p2'}
                  accentColor={accentColor}
                  phase={phase}
                  isEntering={isEntering}
                  startDelay={720}
                />
              </div>
            )}
          </>
        ) : (
          <DensePanelPlaceholder hasName={hasName} placeholderText={placeholderText} />
        )}
      </div>
    </div>
  );
}

// ── D92: Rail panels ──────────────────────────────────────────────────────────

// ---------------------------------------------------------------------------
// DigimonRailPanel — condensed profile rail for Digimon Card Game (~25% col).
// Natural layout (always left-to-right). Side prop controls accent color only.
// Sections: identity (64px circle) → colors → card types → level/form →
//           trait breakdown → attribute breakdown → special mechanics.
// Includes Attribute Breakdown (subtypeDistributions[1]) restored from dense cut.
// ---------------------------------------------------------------------------

export type DigimonRailPanelProps = Omit<DigimonDensePanelProps, 'fullWidth' | 'naturalLayout'>;

export function DigimonRailPanel({
  side,
  stats,
  peerHasStats,
  playerName,
  archetype,
  phase,
  reducedMotion,
  featuredCardImageUrl,
  // Accepted per interface; not consumed (MkDistributionBar is proportional)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  levelSharedMax: _ls,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  levelLabelUnion: _ll,
  placeholderText,
}: DigimonRailPanelProps) {
  const animCls = makeAnimCls(phase);
  const isEntering = phase === 'entering';
  const isP1 = side === 'p1';
  // Rail always natural layout (no P2 mirroring of chart data)
  const accentColor = isP1 ? palette.coralGlow : palette.brilliantRose;
  const accentRgba = isP1 ? p1rgba : p2rgba;
  const hasStats = stats !== null;
  const hasName = playerName.trim().length > 0;
  const isOneDeckPlaceholder = !hasStats && peerHasStats;

  const traitDist = stats?.subtypeDistributions?.[0] ?? null;
  const top5Traits = traitDist?.data
    ? [...traitDist.data].sort((a, b) => b.count - a.count).slice(0, 5)
    : [];
  const traitMax = top5Traits.length > 0 ? Math.max(...top5Traits.map(e => e.count)) : 1;
  const traitHeader = traitDist ? traitDist.label.replace(/ Breakdown$/i, '').toUpperCase() : 'TRAIT BREAKDOWN';

  // Attribute Breakdown (subtypeDistributions[1]) — restored in rail
  const attrDist = stats?.subtypeDistributions?.[1] ?? null;
  const top4Attrs = attrDist?.data
    ? [...attrDist.data].sort((a, b) => b.count - a.count).slice(0, 4)
    : [];
  const attrMax = top4Attrs.length > 0 ? Math.max(...top4Attrs.map(e => e.count)) : 1;
  const attrHeader = attrDist ? attrDist.label.replace(/ Breakdown$/i, '').toUpperCase() : 'ATTRIBUTE BREAKDOWN';

  const mechanics = stats?.specialMechanicCounts ?? null;
  const mechanicsMax = mechanics && mechanics.length > 0 ? Math.max(...mechanics.map(e => e.count)) : 1;
  const levelHeader = stats?.costCurveLabel ? stats.costCurveLabel.toUpperCase() : 'LEVEL / FORM DISTRIBUTION';

  return (
    <div
      aria-hidden="true"
      style={{ flex: 1, height: '100%', position: 'relative', background: glass.card, borderRadius: 16, border: `1px solid ${glassBorder.light}`, overflow: 'hidden' }}
    >
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: accentColor }} />
      <div style={{ position: 'absolute', top: 4, left: 0, right: 0, bottom: 0, padding: '20px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', overflow: 'hidden' }}>

        {/* Identity block */}
        <div className={animCls('dcao-names-enter')} style={{ display: 'flex', gap: 16, alignItems: 'center', minHeight: 80, flexShrink: 0 }}>
          <FeaturedCardAvatarCircle
            imageUrl={featuredCardImageUrl}
            circleSize={64}
            accentColor={accentColor}
            accentRgba={accentRgba}
            isOneDeckPlaceholder={isOneDeckPlaceholder}
            reducedMotion={reducedMotion}
          />
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0, flex: 1, alignItems: 'flex-start' }}>
            {hasName ? (
              <div style={{ fontSize: 28, fontFamily: fonts.display, fontWeight: 700, color: glassText.primary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.0, marginBottom: 2, width: '100%' }}>
                {playerName}
              </div>
            ) : (
              <div style={{ fontSize: 14, fontWeight: 400, color: glassText.muted, marginBottom: 2 }}>{isP1 ? 'Player 1' : 'Player 2'}</div>
            )}
            {archetype && <div style={{ fontSize: 15, fontWeight: 500, color: glassText.secondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>{archetype}</div>}
          </div>
        </div>

        {hasStats ? (
          <>
            <SectionDivider margin="12px 0" />
            <div style={{ flexShrink: 0 }}>
              <MiniHeader text="COLOR" />
              <ColorPips dist={stats.colorDistribution} side="p1" phase={phase} />
            </div>
            <SectionDivider margin="12px 0" />
            {stats.typeDistribution.length > 0 && (
              <>
                <div style={{ flexShrink: 0 }}>
                  <MiniHeader text="CARD TYPES" />
                  <DonutTypeChart dist={stats.typeDistribution} side="p1" phase={phase} size={90} legendPosition="side" showTypePercentages={true} />
                </div>
                <SectionDivider margin="12px 0" />
              </>
            )}
            {stats.costCurve.length > 0 && (
              <>
                <div style={{ flexShrink: 0 }}>
                  <MiniHeader text={levelHeader} />
                  <MkDistributionBar curve={stats.costCurve} side="p1" accentColor={accentColor} phase={phase} reverse={false} />
                </div>
                <SectionDivider margin="12px 0" />
              </>
            )}
            {top5Traits.length > 0 && (
              <>
                <div style={{ flexShrink: 0 }}>
                  <MiniHeader text={traitHeader} />
                  <MiniHorizontalBars entries={top5Traits} localMax={traitMax} side="p1" accentColor={accentColor} phase={phase} isEntering={isEntering} startDelay={650} />
                </div>
                <SectionDivider margin="12px 0" />
              </>
            )}
            {top4Attrs.length > 0 && (
              <>
                <div style={{ flexShrink: 0 }}>
                  <MiniHeader text={attrHeader} />
                  <MiniHorizontalBars entries={top4Attrs} localMax={attrMax} side="p1" accentColor={accentColor} phase={phase} isEntering={isEntering} startDelay={680} />
                </div>
                <SectionDivider margin="12px 0" />
              </>
            )}
            {mechanics && mechanics.length > 0 && (
              <div style={{ flexShrink: 0 }}>
                <MiniHeader text="SPECIAL MECHANICS" />
                <MiniHorizontalBars entries={mechanics} localMax={mechanicsMax} side="p1" accentColor={accentColor} phase={phase} isEntering={isEntering} startDelay={710} />
              </div>
            )}
          </>
        ) : (
          <DensePanelPlaceholder hasName={hasName} placeholderText={placeholderText} />
        )}
      </div>
    </div>
  );
}

